import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import Select, { components } from 'react-select';
import { toast } from 'react-toastify';
import { addMultipleNewUser } from 'services/registration';
import { APIErrors } from 'types/api';
import { Workplace } from 'views/employee-management/useEmployees';
import { ReactComponent as ArrowDown } from '../../../assets/images/icon/icon-down.svg';
import { Input } from '../input';
import cls from './addMutipleNewUser.module.scss';
import { schema } from './validation';
import { useEffect } from 'react';

export const DropdownIndicator = (props) => {
    return (
        <>
            <components.DropdownIndicator {...props}>
                <ArrowDown />
            </components.DropdownIndicator>
        </>
    );
};

export interface AddMultipleNewUserValue {
    count: number | null;
    workplace_id: string;
    department: string;
}

const defaultValues = {
    count: null,
    workplace_id: '',
    department: '',
};

export type AddMultipleNewUserRequest = {
    data: AddMultipleNewUserValue;
};

interface AddNewUserFormInterface {
    adminWorkplaces: Workplace[];
    selectedWorkplaceId?: number;
    onCancel: (boolean) => void;
}

export const AddMultipleNewUserForm = ({
    adminWorkplaces,
    selectedWorkplaceId,
    onCancel
}: AddNewUserFormInterface) => {
    const { t } = useTranslation();

    const { register, handleSubmit, trigger, watch, control, setError, formState: { errors } } = useForm<AddMultipleNewUserValue>({
        resolver: yupResolver(schema),
        defaultValues: {
            ...defaultValues,
            workplace_id: selectedWorkplaceId?.toString() || adminWorkplaces[0]?.id.toString() || '',
        },
        mode: 'onChange',
    });

    const onSubmit = (formObject: AddMultipleNewUserValue) => {
        const prepareData = formObject;

        addNewUserData.mutate({
            data: prepareData,
        });
    };

    const addNewUserData = useMutation<
        AxiosResponse<Blob>,
        AxiosError<APIErrors>,
        AddMultipleNewUserRequest
    >((payload) => addMultipleNewUser(payload), {
        onSuccess: (data) => {
            const url = window.URL.createObjectURL(data.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'login-and-password.pdf';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            toast.success(t('employeeManagement.generateLoginAndPasswordSuccessMessage'));
            onCancel(true);
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof AddMultipleNewUserValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const countValue = watch('count');
    useEffect(() => {
        if (countValue !== null) {
            trigger('count');
        }
    }, [countValue, trigger]);

    return (
        <>
            <h3 className={cls.modalHeader}>
                {t('employeeManagement.addMultipleNewUser')}
            </h3>
            <p className={cls.modalDescription}>
                {t('employeeManagement.addMultipleNewUserDescription')}
            </p>
            <form
                className={cls.form}
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <Input
                    className={cls.inputFull}
                    register={register}
                    id="count"
                    name="count"
                    label={t('employeeManagement.count')}
                    error={t(errors.count?.message)}
                    maxLength={255}
                />
                <div className={cls.selectCustomWrapper}>
                    <p>{t('employeeManagement.workplace_id')}</p>
                    <Controller
                        name="workplace_id"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                className="react-select-container"
                                options={adminWorkplaces}
                                getOptionLabel={(option: Workplace) => option.workplace_name}
                                getOptionValue={(option: Workplace) => option.id.toString()}
                                placeholder={t('employeeManagement.workplace_id')}
                                onChange={(option: Workplace | null) => field.onChange(option?.id ?? '')}
                                value={adminWorkplaces.find(w => w.id === Number(field.value)) || null}
                                components={{ DropdownIndicator }}
                                classNamePrefix="react-select"
                            />
                        )}
                    />
                </div>
                <div className={cls.modalButtonWrapper}>
                    <Button
                        className={cls.modalButton}
                        styleType="border"
                        type="button"
                        onClick={onCancel}
                    >
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.sendAndGenerate')}
                    </Button>
                </div>
            </form>
        </>
    );
};